import React, { useState, useEffect } from 'react';
import './ContactAdmin.css';

function ContactAdmin() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/contacts');
      const result = await response.json();
      
      if (result.success) {
        setContacts(result.data);
      } else {
        setError('Failed to fetch contacts');
      }
    } catch (err) {
      setError('Error connecting to server');
    } finally {
      setLoading(false);
    }
  };

  const updateContactStatus = async (id, newStatus) => {
    try {
      const response = await fetch(`/api/contacts/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      const result = await response.json();
      
      if (result.success) {
        setContacts(prev => 
          prev.map(contact => 
            contact.id === id ? { ...contact, status: newStatus } : contact
          )
        );
      }
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  const filteredContacts = filter === 'all' 
    ? contacts 
    : contacts.filter(contact => contact.status === filter);

  const statusColors = {
    new: '#10b981',
    contacted: '#3b82f6', 
    qualified: '#f59e0b',
    closed: '#6b7280'
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      timeZone: 'Asia/Jakarta',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="contact-admin">
        <div className="contact-admin__loading">
          <div className="spinner"></div>
          <p>Loading contacts...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="contact-admin">
        <div className="contact-admin__error">
          <p>❌ {error}</p>
          <button onClick={fetchContacts} className="btn btn-primary">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="contact-admin">
      <div className="contact-admin__header">
        <h1>📊 Contact Management</h1>
        <div className="contact-admin__stats">
          <div className="stat-card">
            <div className="stat-number">{contacts.length}</div>
            <div className="stat-label">Total Contacts</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">
              {contacts.filter(c => c.status === 'new').length}
            </div>
            <div className="stat-label">New Leads</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">
              {contacts.filter(c => c.status === 'contacted').length}
            </div>
            <div className="stat-label">Contacted</div>
          </div>
        </div>
      </div>

      <div className="contact-admin__filters">
        <button 
          className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All ({contacts.length})
        </button>
        <button 
          className={`filter-btn ${filter === 'new' ? 'active' : ''}`}
          onClick={() => setFilter('new')}
        >
          New ({contacts.filter(c => c.status === 'new').length})
        </button>
        <button 
          className={`filter-btn ${filter === 'contacted' ? 'active' : ''}`}
          onClick={() => setFilter('contacted')}
        >
          Contacted ({contacts.filter(c => c.status === 'contacted').length})
        </button>
        <button 
          className={`filter-btn ${filter === 'qualified' ? 'active' : ''}`}
          onClick={() => setFilter('qualified')}
        >
          Qualified ({contacts.filter(c => c.status === 'qualified').length})
        </button>
        <button 
          className={`filter-btn ${filter === 'closed' ? 'active' : ''}`}
          onClick={() => setFilter('closed')}
        >
          Closed ({contacts.filter(c => c.status === 'closed').length})
        </button>
      </div>

      <div className="contact-admin__table">
        <div className="table-header">
          <div>Name</div>
          <div>Email</div>
          <div>Phone</div>
          <div>Company</div>
          <div>Service</div>
          <div>Status</div>
          <div>Date</div>
          <div>Actions</div>
        </div>
        
        {filteredContacts.length === 0 ? (
          <div className="no-contacts">
            <p>No contacts found</p>
          </div>
        ) : (
          filteredContacts.map(contact => (
            <div key={contact.id} className="contact-row">
              <div className="contact-name">
                <strong>{contact.name}</strong>
              </div>
              <div className="contact-email">
                <a href={`mailto:${contact.email}`}>{contact.email}</a>
              </div>
              <div className="contact-phone">
                {contact.phone || '-'}
              </div>
              <div className="contact-company">
                {contact.company || '-'}
              </div>
              <div className="contact-service">
                {contact.service_type || 'General'}
              </div>
              <div className="contact-status">
                <select 
                  value={contact.status}
                  onChange={(e) => updateContactStatus(contact.id, e.target.value)}
                  className="status-select"
                  style={{ color: statusColors[contact.status] }}
                >
                  <option value="new">New</option>
                  <option value="contacted">Contacted</option>
                  <option value="qualified">Qualified</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
              <div className="contact-date">
                {formatDate(contact.created_at)}
              </div>
              <div className="contact-actions">
                <button 
                  className="action-btn view-btn"
                  onClick={() => alert(`Message:\n\n${contact.message}`)}
                >
                  📝 View
                </button>
                <button 
                  className="action-btn email-btn"
                  onClick={() => window.location.href = `mailto:${contact.email}`}
                >
                  📧 Email
                </button>
                {contact.phone && (
                  <button 
                    className="action-btn phone-btn"
                    onClick={() => window.location.href = `tel:${contact.phone}`}
                  >
                    📱 Call
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default ContactAdmin;
